import DOMPurify from "dompurify";
import { marked } from "marked";

import { sanitizeWorkshopHtmlFragment } from "./widgets";

import type {
  BarChartWidget,
  LineChartWidget,
  ListWidget,
  MetricWidget,
  TableWidget,
  Widget,
  WidgetSlot,
  WorkshopPage,
} from "./types";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function markdownToSafeHtml(source: string): string {
  const rendered = marked.parse(source, {
    gfm: true,
    breaks: true,
    async: false,
  }) as string;
  return DOMPurify.sanitize(rendered, {
    ADD_ATTR: ["target", "rel"],
    ADD_TAGS: ["details", "summary"],
  });
}

const STANDALONE_STYLES = `
:root {
  color-scheme: light dark;
  --bg: #f0f4ff;
  --bg2: #e8eef9;
  --fg: #0f172a;
  --card: rgba(255, 255, 255, 0.92);
  --border: rgba(99, 102, 241, 0.18);
  --muted: #64748b;
  --accent: #4f46e5;
  --accent2: #7c3aed;
  --accent-soft: rgba(79, 70, 229, 0.14);
}
@media (prefers-color-scheme: dark) {
  :root {
    --bg: #0a0f1e;
    --bg2: #111827;
    --fg: #e5e7eb;
    --card: rgba(30, 41, 59, 0.85);
    --border: rgba(129, 140, 248, 0.25);
    --muted: #94a3b8;
    --accent: #a5b4fc;
    --accent2: #c4b5fd;
    --accent-soft: rgba(165, 180, 252, 0.18);
  }
}
* { box-sizing: border-box; }
body {
  margin: 0;
  font-family: "Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.55;
  color: var(--fg);
  background: linear-gradient(165deg, var(--bg) 0%, var(--bg2) 45%, var(--bg) 100%);
  min-height: 100vh;
}
.wrap { max-width: 1000px; margin: 0 auto; padding: 2.25rem 1.35rem 3.5rem; }
header {
  margin-bottom: 2rem;
  padding: 1.25rem 1.4rem;
  border-radius: 16px;
  background: var(--card);
  border: 1px solid var(--border);
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.07);
}
h1 {
  font-size: clamp(1.35rem, 2.8vw, 1.65rem);
  font-weight: 750;
  margin: 0 0 0.4rem;
  letter-spacing: -0.03em;
  background: linear-gradient(120deg, var(--fg), var(--accent));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
@media (prefers-color-scheme: dark) {
  h1 { filter: brightness(1.05); }
}
.meta { font-size: 0.8rem; color: var(--muted); margin: 0; }
.desc { margin-top: 0.65rem; font-size: 0.95rem; color: var(--muted); max-width: 72ch; }
section.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 1.15rem 1.35rem 1.3rem;
  margin-bottom: 1.1rem;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  border-left: 3px solid var(--accent);
}
section.card h2, section.card h3 {
  font-size: 0.95rem;
  font-weight: 650;
  margin: 0 0 0.75rem;
  color: var(--fg);
  letter-spacing: -0.01em;
}
.metric-value {
  font-size: 2rem;
  font-weight: 780;
  letter-spacing: -0.04em;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.metric-hint { font-size: 0.8rem; color: var(--muted); margin-top: 0.4rem; }
table.data {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
  border-radius: 10px;
  overflow: hidden;
}
table.data th, table.data td {
  text-align: left;
  padding: 0.55rem 0.65rem;
  border-bottom: 1px solid var(--border);
}
table.data tbody tr:nth-child(even) { background: rgba(79, 70, 229, 0.04); }
@media (prefers-color-scheme: dark) {
  table.data tbody tr:nth-child(even) { background: rgba(129, 140, 248, 0.06); }
}
table.data th {
  font-weight: 650;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  background: rgba(79, 70, 229, 0.07);
}
ul.simple { margin: 0; padding-left: 1.15rem; }
ul.simple li { margin-bottom: 0.55rem; }
.tag {
  display: inline-block;
  font-size: 0.68rem;
  padding: 0.12rem 0.4rem;
  border-radius: 6px;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
}
.workshop-html-export a { color: var(--accent); text-decoration: none; border-bottom: 1px dotted currentColor; }
.workshop-html-export a:hover { border-bottom-style: solid; }
.spaced-top { margin-top: 0.85rem; }
footer {
  margin-top: 2.25rem;
  font-size: 0.75rem;
  color: var(--muted);
  text-align: center;
}
`;

function widgetSection(slot: WidgetSlot): string {
  const w: Widget = slot.widget;
  const title = (() => {
    if ("title" in w && typeof w.title === "string" && w.title.trim()) {
      return escapeHtml(w.title);
    }
    return null;
  })();

  switch (w.type) {
    case "metric": {
      const m = w as MetricWidget;
      let deltaHtml = "";
      if (m.delta) {
        const d = m.delta;
        deltaHtml = `<p class="metric-hint">${escapeHtml(d.label ?? "Δ")} ${d.value}${escapeHtml(d.suffix ?? "")}</p>`;
      }
      return `
<section class="card">
  ${title ? `<h2>${title}</h2>` : ""}
  <div class="metric-value">${escapeHtml(m.value)}</div>
  ${m.hint ? `<p class="metric-hint">${escapeHtml(m.hint)}</p>` : ""}
  ${deltaHtml}
</section>`;
    }
    case "bar-chart": {
      const c = w as BarChartWidget;
      const max = Math.max(1, ...c.data.map((d) => d.value));
      const rows = c.data
        .map((d) => {
          return `<tr><td>${escapeHtml(d.label)}</td><td>${escapeHtml(String(d.value))}${c.unit ? ` ${escapeHtml(c.unit)}` : ""}</td></tr>`;
        })
        .join("");
      return `
<section class="card">
  ${title ? `<h2>${title}</h2>` : ""}
  ${c.hint ? `<p class="metric-hint">${escapeHtml(c.hint)}</p>` : ""}
  <table class="data"><thead><tr><th>类别</th><th>数值 (${escapeHtml(c.unit ?? "")})</th></tr></thead><tbody>${rows}</tbody></table>
  <p class="metric-hint spaced-top">柱图预览：相对比例 ${max} = 100%。</p>
</section>`;
    }
    case "line-chart": {
      const c = w as LineChartWidget;
      const rows = c.data
        .map((d) => {
          return `<tr><td>${escapeHtml(d.x)}</td><td>${escapeHtml(String(d.y))}${c.unit ? ` ${escapeHtml(c.unit)}` : ""}</td></tr>`;
        })
        .join("");
      return `
<section class="card">
  ${title ? `<h2>${title}</h2>` : ""}
  ${c.hint ? `<p class="metric-hint">${escapeHtml(c.hint)}</p>` : ""}
  <table class="data"><thead><tr><th>X</th><th>Y</th></tr></thead><tbody>${rows}</tbody></table>
</section>`;
    }
    case "table": {
      const t = w as TableWidget;
      const heads = t.columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join("");
      const body = t.rows
        .map((row) => {
          const cells = t.columns
            .map((col) => `<td>${escapeHtml(String(row[col.key] ?? ""))}</td>`)
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      return `
<section class="card">
  ${title ? `<h2>${title}</h2>` : ""}
  ${t.hint ? `<p class="metric-hint">${escapeHtml(t.hint)}</p>` : ""}
  <table class="data"><thead><tr>${heads}</tr></thead><tbody>${body}</tbody></table>
</section>`;
    }
    case "list": {
      const l = w as ListWidget;
      const items = l.items
        .map((it) => {
          const tag = it.tag ? `<span class="tag">${escapeHtml(it.tag)}</span> ` : "";
          const meta = it.meta ? ` <span class="metric-hint">${escapeHtml(it.meta)}</span>` : "";
          const body = it.body ? `<div class="metric-hint">${escapeHtml(it.body)}</div>` : "";
          return `<li>${tag}<strong>${escapeHtml(it.title)}</strong>${meta}${body}</li>`;
        })
        .join("");
      return `
<section class="card">
  ${title ? `<h2>${title}</h2>` : ""}
  ${l.hint ? `<p class="metric-hint">${escapeHtml(l.hint)}</p>` : ""}
  <ul class="simple">${items}</ul>
</section>`;
    }
    case "markdown": {
      const inner = markdownToSafeHtml(w.body);
      return `
<section class="card workshop-html-export">
  ${title ? `<h2>${title}</h2>` : ""}
  <div class="md-body">${inner}</div>
</section>`;
    }
    case "html": {
      const inner = sanitizeWorkshopHtmlFragment(w.html);
      return `
<section class="card workshop-html-export">
  ${title ? `<h2>${title}</h2>` : ""}
  <div class="workshop-html">${inner}</div>
</section>`;
    }
    default:
      return "";
  }
}

function ensureHtmlDocument(inner: string): string {
  const t = inner.trim().toLowerCase();
  if (t.startsWith("<!doctype") || t.startsWith("<html")) {
    return inner;
  }
  return `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="utf-8"><title>Workshop</title></head><body><div class="wrap">${inner}</div></body></html>`;
}

/**
 * Sanitize a full HTML document for ``blob:`` / new-tab preview. Allows
 * ``<style>`` in ``<head>``; strips scripts, frames, inline event handlers,
 * and inline element styles.
 */
export function sanitizeStandaloneHtmlDocument(raw: string): string {
  const doc = ensureHtmlDocument(raw);
  return DOMPurify.sanitize(doc, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ["details", "summary", "style"],
    ADD_ATTR: ["target", "rel", "colspan", "rowspan", "open", "charset", "lang", "content"],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "form",
      "link",
      "base",
      "textarea",
      "input",
    ],
    FORBID_ATTR: ["style"],
  });
}

/**
 * Build a standalone HTML5 page from the current widget grid (tables / lists /
 * sanitized HTML / markdown). Used when ``standalone_html`` is absent or
 * after local edits invalidated the server copy.
 */
export function buildStandaloneHtmlFromPage(page: WorkshopPage): string {
  const kb = page.kb_name ? `知识库 · ${escapeHtml(page.kb_name)}` : "Workshop 导出";
  const desc = page.description ? `<p class="desc">${escapeHtml(page.description)}</p>` : "";
  const sections = page.widgets.map(widgetSection).filter(Boolean).join("\n");
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(page.title)}</title>
  <style>${STANDALONE_STYLES}</style>
</head>
<body>
  <div class="wrap">
    <header>
      <h1>${escapeHtml(page.title)}</h1>
      <p class="meta">${kb} · 导出时间 ${escapeHtml(new Date().toLocaleString())}</p>
      ${desc}
    </header>
    ${sections}
    <footer>由 Knode Workshop 根据当前页面模块生成；图表在导出中为数据表形式。</footer>
  </div>
</body>
</html>`;
}

/**
 * Open the workshop page as a full document in a new browser tab.
 *
 * Implementation notes:
 * - Prefers ``window.open("")`` + ``document.write`` over ``blob:`` URLs.
 *   ``blob:`` + ``noopener`` is unreliable on Chromium (silent blank tab) and
 *   needs ``URL.revokeObjectURL`` bookkeeping.
 * - Falls back to a ``blob:`` URL when ``document.write`` is blocked (e.g.
 *   sandboxed iframes, strict mobile browsers).
 */
export function openWorkshopFullPage(page: WorkshopPage): boolean {
  const raw =
    typeof page.standalone_html === "string" && page.standalone_html.trim().length > 0
      ? page.standalone_html.trim()
      : buildStandaloneHtmlFromPage(page);
  const safe = sanitizeStandaloneHtmlDocument(raw);
  const win = window.open("", "_blank");
  if (win?.document) {
    try {
      win.document.open();
      win.document.write(safe);
      win.document.close();
      return true;
    } catch {
      // fall through to blob fallback
    }
  }
  try {
    const blob = new Blob([safe], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const handle = win ?? window.open(url, "_blank");
    if (win) {
      win.location.href = url;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    return handle != null;
  } catch {
    return false;
  }
}
